import React from 'react'
import { Combobox as HeadlessCombobox, Transition } from '@headlessui/react'
import { SelectorIcon, CheckIcon } from '@heroicons/react/outline'
import { classNames } from 'lib/utils'

type ComboboxProps = {
    label?: string
    placeholder?: string
    options: any[]
    value: any | undefined
    onChange: (option: any | undefined) => void
    display?: (option: any) => string
    description?: string
}

export const Combobox = (props: ComboboxProps) => {
    const [query, setQuery] = React.useState<string>('')
    const [focused, setFocused] = React.useState<boolean>(false)

    const filteredOptions =
        query === ''
            ? props.options
            : props.options.filter((option) =>
                  option.toLowerCase().replace(/\s+/g, '').includes(query.replace(/\s+/g, '').toLowerCase())
              )

    const currentValue = props.display ? props.display(props.value) : props.value

    return (
        <HeadlessCombobox
            as="div"
            className="relative focus:outline-none"
            value={props.value}
            onChange={(value) => {
                props.onChange(value)
                setQuery('')
            }}
            nullable
        >
            {({ open }) => (
                <>
                    {props.label && <HeadlessCombobox.Label className="text-sm">{props.label}</HeadlessCombobox.Label>}
                    <HeadlessCombobox.Button
                        as="div"
                        className="relative mt-1.5 flex w-full items-center shadow-sm focus:outline-none"
                    >
                        <HeadlessCombobox.Input
                            autoComplete="off"
                            onBlur={() => setFocused(false)}
                            onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                                event.target.value = ''
                                setFocused(true)
                            }}
                            onClick={(event: React.MouseEvent<HTMLInputElement>) =>
                                ((event.target as HTMLInputElement).value = '')
                            }
                            onChange={(event) => setQuery(event.target.value)}
                            displayValue={props.display}
                            placeholder={currentValue || props.placeholder || 'Select a value'}
                            className={`placeholder:text-gray-600 relative block w-full select-none rounded border border-black/10 bg-white px-2.5 py-1.5 text-left text-sm focus:border-orange focus:ring-1 focus:ring-orange focus-visible:outline-none dark:bg-gray-accent-dark ${
                                focused ? '' : 'cursor-pointer'
                            }`}
                        />

                        <span className="pointer-events-none absolute right-0 ml-3 pr-2">
                            <SelectorIcon className="h-4 w-4 text-gray-accent-light" aria-hidden="true" />
                        </span>
                    </HeadlessCombobox.Button>
                    {props.description && <p className="m-0 mt-1.5 text-sm  dark:text-white/50">{props.description}</p>}
                    <Transition
                        show={open}
                        as={React.Fragment}
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <HeadlessCombobox.Options className="absolute top-full z-50 mt-1 max-h-[12rem] w-full space-y-1 overflow-y-scroll rounded border border-black/10 bg-white p-0 py-1 text-sm shadow-xl focus:outline-none dark:bg-gray-accent-dark">
                            {filteredOptions.length === 0 && query !== '' ? (
                                <div className="px-2.5 py-1 text-sm text-gray">No results</div>
                            ) : (
                                filteredOptions.map((option) => (
                                    <HeadlessCombobox.Option
                                        value={option}
                                        key={option}
                                        className={({ active }) => `
                                            cursor-pointer list-none px-2.5 py-1 text-sm focus:outline-none
                                            ${active ? 'bg-orange text-white' : ''}
                                        `}
                                    >
                                        {({ selected, active }) => (
                                            <div className="flex items-center justify-between">
                                                <span
                                                    className={classNames(
                                                        selected ? 'font-semibold' : 'font-normal',
                                                        'ml-1 block truncate text-sm'
                                                    )}
                                                >
                                                    {props.display ? props.display(option) : option}
                                                </span>

                                                {selected && (
                                                    <span
                                                        className={classNames(
                                                            'flex items-center',
                                                            active ? 'text-white' : 'text-orange'
                                                        )}
                                                    >
                                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </HeadlessCombobox.Option>
                                ))
                            )}
                        </HeadlessCombobox.Options>
                    </Transition>
                </>
            )}
        </HeadlessCombobox>
    )
}

export default Combobox
